import { TYPEFACES } from "./theme";
/**
 * Web fonts models reach for as unchosen defaults. First-wave (Inter, Roboto, Poppins) and the
 * second-wave substitutes that appeared after those were banned (Geist, Space Grotesk, Instrument
 * Serif, Fraunces, Newsreader, Outfit, DM Sans, Nunito).
 */
export const BANNED_WEBFONT_NAMES = [
    "Inter",
    "Geist Mono",
    "Geist",
    "Space Grotesk",
    "Space Mono",
    "Instrument Serif",
    "Instrument Sans",
    "Fraunces",
    "Newsreader",
    "Playfair Display",
    "Playfair",
    "Outfit",
    "DM Sans",
    "Nunito Sans",
    "Nunito",
    "Roboto",
    "Open Sans",
    "Poppins",
    "Montserrat",
    "Plus Jakarta Sans",
];
function stack(kind) {
    return kind === "serif" ? "Georgia, serif" : "system-ui, sans-serif";
}
function pairing(display, displayKind, body, bodyKind, weight, href) {
    return {
        display,
        body,
        displayFamily: `'${display}', ${stack(displayKind)}`,
        bodyFamily: `'${body}', ${stack(bodyKind)}`,
        weight,
        href,
    };
}
/**
 * Curated Google Fonts pairings for builder sites. Three options per typeface id so the designer
 * can pick for the brief instead of falling back to training-data defaults. Only the first pairing
 * is shown in the brand picker.
 */
export const TYPEFACE_GUIDES = {
    editorial: {
        name: "Editorial",
        description: "Warm serif headings over a clean sans",
        pairings: [
            pairing("Young Serif", "serif", "Figtree", "sans", 400, "family=Figtree:wght@400;500&family=Young+Serif"),
            pairing("Source Serif 4", "serif", "Figtree", "sans", 600, "family=Figtree:wght@400;500&family=Source+Serif+4:wght@400;600"),
            pairing("Gloock", "serif", "Figtree", "sans", 400, "family=Figtree:wght@400;500&family=Gloock"),
        ],
    },
    classic: {
        name: "Classic",
        description: "Book serif headings over a clean sans",
        pairings: [
            pairing("Libre Baskerville", "serif", "Karla", "sans", 700, "family=Karla:wght@400;600&family=Libre+Baskerville:wght@400;700"),
            pairing("EB Garamond", "serif", "Libre Franklin", "sans", 600, "family=Libre+Franklin:wght@400&family=EB+Garamond:wght@500;600"),
            pairing("Bodoni Moda", "serif", "Karla", "sans", 600, "family=Karla:wght@400;600&family=Bodoni+Moda:opsz,wght@6..96,600"),
        ],
    },
    literary: {
        name: "Literary",
        description: "Reading serif throughout",
        pairings: [
            pairing("Literata", "serif", "Literata", "serif", 500, "family=Literata:opsz,wght@7..72,400;7..72,500"),
            pairing("Spectral", "serif", "Spectral", "serif", 600, "family=Spectral:wght@400;600"),
            pairing("Source Serif 4", "serif", "Source Serif 4", "serif", 600, "family=Source+Serif+4:wght@400;600"),
        ],
    },
    modern: {
        name: "Modern",
        description: "Neutral grotesk",
        pairings: [
            pairing("Hanken Grotesk", "sans", "Hanken Grotesk", "sans", 700, "family=Hanken+Grotesk:wght@400;700"),
            pairing("Schibsted Grotesk", "sans", "Schibsted Grotesk", "sans", 700, "family=Schibsted+Grotesk:wght@400;700"),
            pairing("Public Sans", "sans", "Public Sans", "sans", 700, "family=Public+Sans:wght@400;700"),
        ],
    },
    geometric: {
        name: "Geometric",
        description: "Crisp geometric sans",
        pairings: [
            pairing("Red Hat Display", "sans", "Red Hat Text", "sans", 600, "family=Red+Hat+Text:wght@400&family=Red+Hat+Display:wght@600"),
            pairing("League Spartan", "sans", "Albert Sans", "sans", 600, "family=Albert+Sans:wght@400&family=League+Spartan:wght@600"),
            pairing("Darker Grotesque", "sans", "Figtree", "sans", 700, "family=Figtree:wght@400;500&family=Darker+Grotesque:wght@500;700"),
        ],
    },
    friendly: {
        name: "Friendly",
        description: "Warm, even sans",
        pairings: [
            pairing("Onest", "sans", "Onest", "sans", 800, "family=Onest:wght@400;800"),
            pairing("Atkinson Hyperlegible", "sans", "Atkinson Hyperlegible", "sans", 700, "family=Atkinson+Hyperlegible:wght@400;700"),
            pairing("Varela Round", "sans", "Figtree", "sans", 400, "family=Figtree:wght@400;500&family=Varela+Round"),
        ],
    },
    statement: {
        name: "Statement",
        description: "Heavy, poster-like headings",
        pairings: [
            pairing("Archivo Black", "sans", "Archivo", "sans", 400, "family=Archivo+Black&family=Archivo:wght@400"),
            pairing("Anton", "sans", "Archivo", "sans", 400, "family=Anton&family=Archivo:wght@400"),
            pairing("Big Shoulders Display", "sans", "Archivo", "sans", 700, "family=Archivo:wght@400&family=Big+Shoulders+Display:wght@700"),
        ],
    },
};
function guideFor(id) {
    const guide = TYPEFACE_GUIDES[id];
    if (!guide)
        throw new Error(`Unknown typeface: ${id}`);
    return guide;
}
export function typefaceSample(id) {
    const guide = guideFor(id);
    return { name: guide.name, description: guide.description, ...guide.pairings[0] };
}
/** Google Fonts stylesheet for the brand picker (one pairing per typeface id). */
export function typefacePickerHref() {
    const hrefs = Object.keys(TYPEFACES).map((id) => guideFor(id).pairings[0].href);
    return `https://fonts.googleapis.com/css2?${hrefs.join("&")}&display=swap`;
}
export function typePairingsCatalogText() {
    return Object.keys(TYPEFACE_GUIDES).map((id) => {
        const rows = guideFor(id).pairings.map((item) => item.display === item.body ? item.display : `${item.display} + ${item.body}`);
        return `${id}: ${rows.join("; ")}`;
    }).join("\n");
}
export function typePairingsForBrief(typeface) {
    const ids = typeface ? [typeface] : Object.keys(TYPEFACE_GUIDES);
    return ids.flatMap((id) => guideFor(id).pairings.map((item) => ({
        id,
        display: item.display,
        body: item.body,
        href: item.href,
    })));
}
/** A Google Fonts family name as a brand stores it, e.g. "Source Serif 4". */
export const FONT_FAMILY = /^[A-Za-z0-9][A-Za-z0-9 ]{0,59}$/;
/** A Google Fonts stylesheet the server built for a brand's fonts. */
export const FONT_HREF = /^https:\/\/fonts\.googleapis\.com\/css2\?[A-Za-z0-9+:@;,.=&_-]{1,600}$/;
const SERIF_FAMILIES = new Set(Object.values(TYPEFACE_GUIDES).flatMap((guide) => guide.pairings.flatMap((item) => [
    ...(item.displayFamily.endsWith("serif") && !item.displayFamily.endsWith("sans-serif") ? [item.display] : []),
    ...(item.bodyFamily.endsWith("serif") && !item.bodyFamily.endsWith("sans-serif") ? [item.body] : []),
])));
/** A family with a fallback: serif for known or named serifs, otherwise the system sans. */
export function fontStack(family) {
    const serif = SERIF_FAMILIES.has(family) || (/serif/i.test(family) && !/sans/i.test(family));
    return `'${family}', ${stack(serif ? "serif" : "sans")}`;
}
/** One `family=` value for the Google Fonts css2 API, e.g. "Source+Serif+4:wght@400;700". */
export function fontFamilyParam(family, weights) {
    return `${family.trim().replace(/ +/g, "+")}${weights ? `:wght@${weights}` : ""}`;
}
/** A css2 stylesheet for the given `family=` values. */
export function fontsHref(families) {
    return `https://fonts.googleapis.com/css2?${[...new Set(families)].map((family) => `family=${family}`).join("&")}&display=swap`;
}
/** Every family the curated pairings use, for suggestions when the owner picks their own. */
export function catalogFontFamilies() {
    return [...new Set(Object.values(TYPEFACE_GUIDES).flatMap((guide) => guide.pairings.flatMap((item) => [item.display, item.body])))].sort();
}
/** The fonts a typeface style starts from: its first pairing. */
export function typefaceFonts(id) {
    const { display, body } = guideFor(id).pairings[0];
    return { display, body };
}
